import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ServiceQueuePanel } from "@/components/queue/ServiceQueuePanel";
import { PageHeader } from "@/components/PageHeader";
import { TablePagination } from "@/components/TablePagination";
import { useClientPagination } from "@/hooks/useClientPagination";
import {
  consultationDetailPath,
  createConsultation,
  listConsultations,
  syncConsultationsFromApi,
} from "@/core/clinical/clinical-store";
import { CONSULTATION_STATUS_LABELS, formatVitalsSummary } from "@/core/clinical/types";
import { getPatientById, listPatients } from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";
import { routes } from "@/router/all_routes";

export function ConsultationsPage() {
  const navigate = useNavigate();
  const [patientId, setPatientId] = useState("");
  const [motif, setMotif] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    void syncConsultationsFromApi().finally(() => setTick((t) => t + 1));
  }, []);

  const consultations = useMemo(() => listConsultations(), [tick]);
  const patients = useMemo(() => listPatients(), [tick]);
  const pagination = useClientPagination(consultations, [], 25);
  const { pagedItems } = pagination;

  const handleCreate = (e: FormEvent) => {
    e.preventDefault();
    if (!patientId) {
      setFormError("Sélectionnez un patient.");
      return;
    }
    if (!motif.trim()) {
      setFormError("Indiquez le motif de consultation.");
      return;
    }
    const created = createConsultation({ patientId, motif });
    setMotif("");
    setFormError(null);
    navigate(consultationDetailPath(created.id));
  };

  return (
    <div>
      <PageHeader
        title="Consultations"
        description="Salle d'attente clinique — appel par box, puis consultation et ordonnance."
        actions={
          <Link to={routes.prescriptions} className="btn btn-outline-secondary btn-sm">
            Ordonnances
          </Link>
        }
      />

      <ServiceQueuePanel
        serviceId="clinical"
        defaultUnitName="Médecine générale"
        enableCallNext
        title="Salle d'attente — clinique"
      />

      <div className="ph-invoice-sheet mb-4">
        <h3 className="h6 fw-semibold mb-3">Nouvelle consultation</h3>
        <form className="row g-3 align-items-end ph-lab-register-form" onSubmit={handleCreate}>
          <div className="col-12 col-md-5">
            <label className="form-label small fw-semibold mb-1" htmlFor="con-patient">
              Patient
            </label>
            <select
              id="con-patient"
              className="form-select form-select-sm"
              value={patientId}
              onChange={(e) => setPatientId(e.target.value)}
            >
              <option value="">— Choisir —</option>
              {patients.map((p) => (
                <option key={p.id} value={p.id}>
                  {patientFullName(p)} ({p.fileNumber})
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 col-md-5">
            <label className="form-label small fw-semibold mb-1" htmlFor="con-motif">
              Motif
            </label>
            <input
              id="con-motif"
              type="text"
              className="form-control form-control-sm"
              placeholder="Motif de la visite"
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
            />
          </div>
          <div className="col-12 col-md-2">
            <button type="submit" className="btn btn-primary btn-sm w-100">
              Ouvrir
            </button>
          </div>
        </form>
        {formError && <p className="small text-danger mb-0 mt-2">{formError}</p>}
      </div>

      <div className="ph-invoice-sheet">
        {consultations.length === 0 ? (
          <p className="text-muted mb-0">Aucune consultation enregistrée.</p>
        ) : (
          <div className="table-responsive ph-diag-table">
            <table className="table table-sm ph-dash-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Patient</th>
                  <th>Motif</th>
                  <th>Constantes</th>
                  <th>Statut</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedItems.map((consultation) => {
                  const patient = getPatientById(consultation.patientId);
                  return (
                    <tr key={consultation.id} className="ph-diag-row">
                      <td data-label="Date" className="small text-muted">
                        {new Date(consultation.createdAt).toLocaleString("fr-FR")}
                      </td>
                      <td data-label="Patient" className="fw-medium">
                        {patient ? (
                          <Link to={consultationDetailPath(consultation.id)}>
                            {patientFullName(patient)}
                          </Link>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td data-label="Motif" className="small">
                        {consultation.motif || "—"}
                      </td>
                      <td data-label="Constantes" className="small text-muted">
                        {formatVitalsSummary(consultation.vitals) || "—"}
                      </td>
                      <td data-label="Statut">
                        <span
                          className={`hosp-clinical-badge hosp-clinical-badge--${consultation.status}`}
                        >
                          {CONSULTATION_STATUS_LABELS[consultation.status]}
                        </span>
                      </td>
                      <td data-label="Actions" className="text-end">
                        <Link
                          to={consultationDetailPath(consultation.id)}
                          className="btn btn-link btn-sm p-0"
                        >
                          Ouvrir
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <TablePagination
              page={pagination.page}
              pageSize={pagination.pageSize}
              totalItems={pagination.totalItems}
              onPageChange={pagination.setPage}
              onPageSizeChange={pagination.setPageSize}
            />
          </div>
        )}
      </div>
    </div>
  );
}
